import { useEffect } from "react";
import mapboxgl from "mapbox-gl";

export const usePolygonManagement = ({ mapInstance, polygons }) => {
    // Función para agregar polígonos al mapa
    const addPolygonsToMap = (map) => {
        if (!polygons?.length) return;

        const features = polygons.map((poly, idx) => ({
            type: "Feature",
            properties: { ...poly, id: poly.siep_code || idx },
            geometry: {
                type: "Polygon",
                coordinates: [
                    poly.geometry.coordinates.map(([lat, lng]) => [lng, lat]),
                ],
            },
        }));

        const geojson = { type: "FeatureCollection", features };

        if (!map.getSource("custom-polygons")) {
            map.addSource("custom-polygons", { type: "geojson", data: geojson });

            map.addLayer({
                id: "custom-polygons-fill",
                type: "fill",
                source: "custom-polygons",
                paint: { "fill-color": "#0D1C36", "fill-opacity": 0.3 },
            });

            map.addLayer({
                id: "custom-polygons-outline",
                type: "line",
                source: "custom-polygons",
                paint: { "line-color": "#000E27", "line-width": 1 },
            });

            const popup = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
            });

            map.on("mouseenter", "custom-polygons-fill", (e) => {
                const { coordinates } = e.features[0].geometry;
                const npert = e.features[0].properties.N_PERT;
                popup
                    .setLngLat(coordinates[0][0])
                    .setHTML(`<strong>N_PERT:</strong> ${npert}`)
                    .addTo(map);
            });

            map.on("mouseleave", "custom-polygons-fill", () => popup.remove());
        } else {
            map.getSource("custom-polygons").setData(geojson);
        }
    };

    // Función para limpiar capas de polígonos
    const cleanupPolygonLayers = (map) => {
        try {
            if (map.getLayer("custom-polygons-fill")) {
                map.removeLayer("custom-polygons-fill");
            }
            if (map.getLayer("custom-polygons-outline")) {
                map.removeLayer("custom-polygons-outline");
            }
            if (map.getSource("custom-polygons")) {
                map.removeSource("custom-polygons");
            }
        } catch (error) {
        }
    };

    const addPolygonsImproved = (map) => {
        if (!map || !map.isStyleLoaded || !map.isStyleLoaded()) {
            setTimeout(() => addPolygonsImproved(map), 100);
            return;
        }

        cleanupPolygonLayers(map);

        if (!polygons || polygons.length === 0) {
            return;
        }

        const features = polygons.map((poly, idx) => {
            let coords = poly.geometry.coordinates.map(([lat, lng]) => [lng, lat]);
            if (coords.length > 2) {
                const first = coords[0];
                const last = coords[coords.length - 1];
                if (first[0] !== last[0] || first[1] !== last[1]) {
                    coords = [...coords, first];
                }
            }
            return {
                type: "Feature",
                properties: {
                    ...poly,
                    id: poly.siep_code || idx,
                },
                geometry: {
                    type: "Polygon",
                    coordinates: [coords],
                },
            };
        });

        const geojson = {
            type: "FeatureCollection",
            features,
        };

        try {
            map.addSource("custom-polygons", {
                type: "geojson",
                data: geojson,
            });

            map.addLayer({
                id: "custom-polygons-fill",
                type: "fill",
                source: "custom-polygons",
                paint: {
                    "fill-color": "#0D1C36",
                    "fill-opacity": 0.3,
                },
            });

            map.addLayer({
                id: "custom-polygons-outline",
                type: "line",
                source: "custom-polygons",
                paint: {
                    "line-color": "#000E27",
                    "line-width": 1,
                },
            });

            const popup = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
                className: "rounded-4",
            });

            map.off("mouseenter", "custom-polygons-fill");
            map.off("mouseleave", "custom-polygons-fill");

            map.on("mouseenter", "custom-polygons-fill", (e) => {
                const feature = e.features[0];
                const coordinates = feature.geometry.coordinates[0][0];
                const npert = feature.properties.N_PERT;
                popup
                    .setLngLat(coordinates)
                    .setHTML(`<strong>N_PERT:</strong> ${npert}`)
                    .addTo(map);
            });

            map.on("mouseleave", "custom-polygons-fill", () => {
                if (popup) popup.remove();
            });
        } catch (error) {
        }
    };

    useEffect(() => {
        if (!mapInstance) return;

        const map = mapInstance;

        const handleStyleLoad = () => {
            addPolygonsImproved(map);
        };

        map.on("style.load", handleStyleLoad);

        if (map.isStyleLoaded && map.isStyleLoaded()) {
            addPolygonsImproved(map);
        } else {
            map.once("style.load", () => addPolygonsImproved(map));
        }

        // Cleanup function
        return () => {
            if (map && !map._removed) {
                map.off("style.load", handleStyleLoad);
            }
        };
    }, [polygons, mapInstance]);

    return {
        addPolygonsToMap,
        cleanupPolygonLayers,
        addPolygonsImproved,
    };
};
